const m = require('minthril');
const { component } = require('../../component');
const { Breadcrumbs } = require('../../components/breadcrumbs');
const { estimatePricingDomain } = require('../../controllers/pricing');

function formatRenderTime (milliseconds) {
  if (milliseconds < 1000) return `${Math.round(milliseconds)} ms`;
  return `${(milliseconds / 1000).toFixed(1)} seconds`;
}

function formatPrice (amount, currency) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

function renderingAssessmentMessage (stats) {
  const htmlShare = initialHtmlShare(stats);

  if (htmlShare === null) {
    return {
      title: 'Spache might help!',
      description: 'The homepage render did not provide enough readable text to compare the initial HTML with the browser-rendered page.'
    };
  }

  if (htmlShare <= 50) {
    return {
      title: 'Spache could make a big difference!',
      description: 'Less than half of the browser-rendered text was present in the initial HTML. Spache can serve the complete rendered page as HTML to crawlers and visitors.'
    };
  }

  if (htmlShare <= 75) {
    return {
      title: 'Spache might help!',
      description: 'Some meaningful content appeared only after JavaScript ran. Spache can make more of the rendered page available as HTML from the first response.'
    };
  }

  if (htmlShare <= 90) {
    return {
      title: 'Spache might help a little',
      description: 'Most of the browser-rendered text was already in the initial HTML, so the rendering benefit may be modest. Spache can still help with caching and delivery.'
    };
  }

  return {
    title: 'Spache might not be needed',
    description: 'At least 90% of the browser-rendered text was already in the initial HTML, so Spache may not be worth it for rendering. It can still help with caching and delivery.'
  };
}

function initialHtmlShare (stats) {
  if (!Number.isFinite(stats?.initialTextLength) ||
      !Number.isFinite(stats?.renderedTextLength) ||
      stats.renderedTextLength < 80) {
    return null;
  }

  const percentage = Math.min(100,
    stats.initialTextLength / stats.renderedTextLength * 100);
  if (percentage > 0 && percentage < 10) {
    return Math.max(0.1, Math.round(percentage * 10) / 10);
  }

  return Math.round(percentage);
}

const PricingDocsPage = component((vnode, emit) => {
  let domain = '';
  let estimate = null;
  let error = '';
  let loading = false;

  async function submitEstimate (event) {
    event.preventDefault();
    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.set('domain', domain.trim());
    window.history.replaceState({}, '', `${pageUrl.pathname}${pageUrl.search}${pageUrl.hash}`);

    loading = true;
    estimate = null;
    error = '';
    emit('redraw');

    try {
      estimate = await estimatePricingDomain(domain);
    } catch (estimateError) {
      error = estimateError.message;
    } finally {
      loading = false;
      emit('redraw');
    }
  }

  return {
    oninit: () => {
      const pageUrl = new URL(window.location.href);
      const requestedDomain = pageUrl.searchParams.get('domain');
      if (!requestedDomain) return;

      domain = requestedDomain;
      submitEstimate({ preventDefault: () => {} });
    },
    view: () => {
      const renderingStats = estimate?.renderingStats;
      const assessment = renderingAssessmentMessage(renderingStats);
      const htmlShare = initialHtmlShare(renderingStats);

      return m('article.panel.docs-page.pricing-page', [
        m(Breadcrumbs, {
          items: [
            { label: 'Docs', href: '/docs' },
            { label: 'Pricing' }
          ]
        }),
        m('section.pricing-page__calculator', [
          m('h3', 'Estimate monthly cost for your site'),
          !estimate && m('p', 'Enter a public root domain. Spache will render its homepage once and give you an estimate.'),
          !estimate && m('form.pricing-page__form', { onsubmit: submitEstimate }, [
            m('label', { for: 'pricing-domain' }, 'Root domain'),
            m('div.pricing-page__form-controls', [
              m('input#pricing-domain', {
                type: 'text',
                inputmode: 'url',
                autocomplete: 'url',
                placeholder: 'example.com',
                disabled: loading,
                value: domain,
                oninput: event => {
                  domain = event.target.value;
                  emit('redraw');
                }
              }),
              m('button.button', { type: 'submit', disabled: loading || !domain.trim() },
                loading ? 'Rendering…' : 'Estimate')
            ])
          ]),
          error && m('p.pricing-page__error', error),
          estimate && m('div.pricing-page__estimate', [
            (estimate.beforeScreenshot || estimate.afterScreenshot || estimate.screenshot) &&
            m('div.pricing-page__previews', [
              estimate.beforeScreenshot && m('figure.pricing-page__preview', [
                m('figcaption', 'Before Spache · JavaScript off'),
                m('img', {
                  src: `data:image/jpeg;base64,${estimate.beforeScreenshot}`,
                  alt: `Initial HTML view of ${estimate.domain} with JavaScript disabled`,
                  width: 200,
                  height: 400
                })
              ]),
              (estimate.afterScreenshot || estimate.screenshot) && m('figure.pricing-page__preview', [
                m('figcaption', 'After Spache · rendered page'),
                m('img', {
                  src: `data:image/jpeg;base64,${estimate.afterScreenshot || estimate.screenshot}`,
                  alt: `Browser-rendered homepage for ${estimate.domain}`,
                  width: 200,
                  height: 400
                })
              ])
            ]),
            m('div.pricing-page__estimate-content', [
              m('aside.pricing-page__rendering-assessment', [
                m('strong', assessment.title),
                m('p', assessment.description),
                htmlShare !== null && m('div.pricing-page__html-ratio', [
                  m('div.pricing-page__html-ratio-labels', [
                    m('span', `Initial HTML · ${htmlShare}%`),
                    m('span', 'After browser render · 100%')
                  ]),
                  m('div.pricing-page__html-ratio-track', {
                    role: 'progressbar',
                    'aria-label': 'Share of rendered readable text already present in the initial HTML',
                    'aria-valuemin': 0,
                    'aria-valuemax': 100,
                    'aria-valuenow': htmlShare
                  }, m('span', { style: { width: `${htmlShare}%` } })),
                  m('span.pricing-page__html-ratio-detail',
                    `${renderingStats.initialTextLength.toLocaleString()} readable characters before JavaScript · ${renderingStats.renderedTextLength.toLocaleString()} after browser rendering`)
                ])
              ]),
              estimate.renderedAt && m('p.pricing-page__rendered-at',
                `Rendered at ${new Date(estimate.renderedAt).toLocaleString()}`),
              m('p', [
                'Measured render: ',
                m('strong', formatRenderTime(estimate.renderDurationMs)),
                m('span.pricing-page__compute-detail', `Normalized worker compute used for pricing: ${formatRenderTime(estimate.workerComputeMs)} per render.`)
              ]),
              m('div.pricing-page__estimate-results', estimate.monthlyEstimates.map(item => m('article', [
                m('h4', `${item.renderCount.toLocaleString()} renders/month`),
                m('p.pricing-page__estimate-total', [
                  'Estimated total: ~',
                  m('strong', `${formatPrice(item.estimatedTotalUsd, estimate.currency)}/month`)
                ]),
                m('p.pricing-page__note', [
                  `Includes ${formatPrice(estimate.connectedSiteMonthlyUsd, estimate.currency)} connected-site fee and 30 minutes of included compute. `,
                  `Estimated compute overage: ${formatPrice(item.computeOverageUsd, estimate.currency)}. Proxy/data usage not included.`
                ])
              ]))),
              m('p.pricing-page__note', 'Estimate assumes the shared 30-minute monthly compute allowance is otherwise unused. It is based on one render; actual render times and final usage vary.'),
              m('a.button', {
                href: `/sites/new?origin=${encodeURIComponent(`https://${estimate.domain}`)}`
              }, 'Add this site to Spache')
            ])
          ])
        ]),
        m('h2', 'Simple pricing, with room to try Spache for free'),
        m('p', 'Create sites and test them on Spache preview domains without adding payment details. A paid plan is needed to connect custom domains.'),
        m('section.pricing-page__plan', [
          m('h3', 'Free'),
          m('p.pricing-page__price', '$0'),
          m('p', 'Build and test with preview domains, with a shared monthly allowance for your account.'),
          m('ul', [
            m('li', '10,000 proxied requests per account each month'),
            m('li', '1 GiB of response data per account each month'),
            m('li', '30 minutes of normalized rendering and validation compute per account each month'),
            m('li', 'No payment details required; custom domains are not available on the free plan')
          ])
        ]),
        m('section.pricing-page__plan', [
          m('h3', 'Paid'),
          m('p.pricing-page__price', ['$1', m('span', ' / connected site / month')]),
          m('p', 'Connect custom domains and keep using Spache. The included account allowance is applied before usage overages.'),
          m('p', 'After the monthly allowance, usage is charged at the rates below. Overage is calculated for the billing period and charged automatically after the period ends.')
        ]),
        m('h3', 'Included usage and overage rates'),
        m('div.pricing-page__table-wrap', [
          m('table.pricing-page__table', [
            m('thead', m('tr', [
              m('th', 'Usage'),
              m('th', 'Included each month'),
              m('th', 'Overage rate')
            ])),
            m('tbody', [
              m('tr', [m('th', 'Proxied requests'), m('td', '10,000 per account'), m('td', '$0.00001 per request')]),
              m('tr', [m('th', 'Response data served'), m('td', '1 GiB per account'), m('td', '$0.125 per GiB')]),
              m('tr', [m('th', 'Rendering and validation compute'), m('td', '30 minutes per account'), m('td', '$0.00053 per normalized compute second')])
            ])
          ])
        ]),
        m('p.pricing-page__note', 'Allowances are shared across all sites on an account, not granted separately to each site. Usage figures in the app are near-real-time estimates; final usage and overage charges are calculated at the end of the billing period. Prices are in USD and may be subject to applicable taxes.'),
        m('p', [
          'See ',
          m('a', { href: '/docs/features' }, 'Spache features'),
          ' or ',
          m('a', { href: '/docs/put-your-site-in-spache' }, 'get started'),
          '.'
        ])
      ]);
    }
  };
});

module.exports = { PricingDocsPage };
